<script setup lang="ts">
import type { ContactFormModalProps, ContactMethod } from '.';

import { useAdCampaign } from '~/composables/adCampaign';
import { useEvents } from '~/composables/events/useEvents';
import {
	mailSouPilar,
	pilarFormattedPhone,
	pilarFormattedPhoneWhatsAppLink
} from '~/constants';
import { MixpanelProps } from '~/core/service/mixpanel';
import yup from '~/core/validation';
import { nuxtAuthRepository } from '~~/adapters/repositories/nuxt/nuxtAuthRepository';
import { nuxtLeadsRepository } from '~~/adapters/repositories/nuxt/nuxtLeadsRepository';
import { getAgentFirstName, getAgentName } from '~~/utils';
import { useForm } from 'vee-validate';

import { ThirdStep as OTPForm } from '~/components/LoginForm/Steps/ThirdStep';
import { Modal } from '~/components/Modal';
import { DialogTitle } from '~/components/ui/dialog';
import TextLink from '~/components/ui/text-link/TextLink.vue';

import NorthEastIcon from '~icons/material-symbols-light/north-east';
import WestIcon from '~icons/material-symbols-light/west';
import IconMaterialSymbolsCallOutline from '~icons/material-symbols/call-outline';
import CheckCircleIcon from '~icons/material-symbols/check-circle';
import DoneIcon from '~icons/material-symbols/done';
import ErrorCircleIcon from '~icons/material-symbols/error';
import IconMaterialSymbolsMailOutline from '~icons/material-symbols/mail-outline';

import propertyPricesLocale from './ContactFormModal.locale.json';

const { trackLinkClicked } = useEvents();

const props = withDefaults(defineProps<ContactFormModalProps>(), {
	context: 'property'
});

const { t } = useI18n({
	useScope: 'local',
	messages: propertyPricesLocale
});

const isPropertyContext = computed(() => props.context === 'property');

function translateByContext(
	key: string,
	options: Record<string, string | undefined> = {}
): string {
	const baseKey = `${props.context}.${key}`;
	return t(baseKey, options);
}

const isOpen = defineModel<boolean>();
const currentPageName = computed(() =>
	isPropertyContext.value ? 'Detalhe do Imóvel' : 'Detalhe do Condomínio'
);
const {
	trackFormPropertyViewed,
	trackFormPropertyError,
	trackFormPropertyAuthentication,
	trackFormPropertyCompleted,
	trackingGoToPrevious,
	trackingCloseClicked,
	trackSignAndLogin
} = useEvents();

const { getAdCampaign } = useAdCampaign();

const route = useRoute();

function closeModal() {
	isOpen.value = false;
	addEventsCloseModal();
}

const {
	loggedIn: isUserLoggedIn,
	user,
	fetch: fetchUserSession
} = useUserSession();

const schema = yup.object({
	email: yup.string().email(),
	name: yup.string().required(),
	phone: yup.string().phone('Deve ser um telefone válido').required(),
	userMessage: yup.string()
});

type Schema = yup.InferType<typeof schema>;

const {
	handleSubmit,
	defineField,
	setFieldValue,
	errors,
	resetForm,
	meta,
	validate
} = useForm<Schema>({
	initialValues: {
		email: user.value?.email || '',
		name: user.value?.name || '',
		phone: user.value?.phoneNumber || '',
		userMessage: ''
	},
	validationSchema: schema,
	validateOnMount: false
});

const [name, nameAttrs] = defineField('name');
const [email, emailAttrs] = defineField('email');
const [phone, phoneAttrs] = defineField('phone');
const [userMessage, userMessageAttrs] = defineField('userMessage');

const contactMethodProps = [
	{
		label: mailSouPilar,
		route: `mailto:${mailSouPilar}`,
		icon: IconMaterialSymbolsMailOutline
	},
	{
		label: pilarFormattedPhone,
		route: pilarFormattedPhoneWhatsAppLink,
		icon: IconMaterialSymbolsCallOutline
	}
];

const currentStep = ref<
	'contact-form' | 'otp-form' | 'success-message' | 'submit-error-message'
>('contact-form');

function goToContactFormStep() {
	currentStep.value = 'contact-form';
	addEventReturnClicked();
}

function goToSuccessMessage() {
	currentStep.value = 'success-message';
}

function goToSubmitErrorMessage() {
	currentStep.value = 'submit-error-message';
}

function goToOtpFormStep() {
	currentStep.value = 'otp-form';
}

whenever(isOpen, () => {
	if (
		currentStep.value === 'success-message' ||
		currentStep.value === 'submit-error-message'
	) {
		goToContactFormStep();
	}
});

async function createPropertyLead(
	values: Schema,
	adCampaign: Record<string, string>
) {
	if (props.propertyId) {
		return nuxtLeadsRepository.createPropertyLead({
			comments: values.userMessage?.trim() || initialUserMessage.value,
			propertyId: props.propertyId,
			email: values.email || user.value?.email || null,
			name: values.name || user.value?.name || '',
			phoneNumber: values.phone,
			...(adCampaign || {})
		});
	}
}

async function createCondoLead(
	values: Schema,
	adCampaign: Record<string, string>
) {
	if (props.condoId && props.companyDistribution) {
		return nuxtLeadsRepository.createCondoLead({
			comments: values.userMessage?.trim() || initialUserMessage.value,
			canonicalCondoId: props.condoId,
			companyDistribution: props.companyDistribution,
			canonicalCondoName: props.condoName ?? '',
			email: values.email || user.value?.email || null,
			name: values.name || user.value?.name || '',
			phoneNumber: values.phone,
			...(adCampaign || {})
		});
	}
}

const errorMessage = ref<string | null>(null);
const submitForm = handleSubmit(async values => {
	try {
		const adCampaign = getAdCampaign();

		const method = isPropertyContext.value
			? createPropertyLead
			: createCondoLead;

		const [_, error] = await method(values, adCampaign);

		if (error) {
			errorMessage.value = error;
			return goToSubmitErrorMessage();
		}

		goToSuccessMessage();
	} catch (error) {
		const errorConverted = error as Error;
		errorMessage.value = errorConverted?.message;
		goToSubmitErrorMessage();
	}
});

const hasUserRegistryBySocialMethod = computed(
	() =>
		user.value?.login_method === 'google' ||
		user.value?.login_method === 'facebook'
);

const hasUserRegistryByPhoneMethod = computed(
	() => user.value?.login_method === 'phone'
);
const hasUserIncompleteRegistry = computed(
	() => !user.value?.hasCompletedRegistration
);

function getNextStep():
	| 'submit'
	| 'complete_phone'
	| 'complete_social'
	| 'otp' {
	if (!isUserLoggedIn.value) return 'otp';

	if (isUserLoggedIn.value && !hasUserIncompleteRegistry.value) return 'submit';

	if (
		isUserLoggedIn.value &&
		hasUserRegistryBySocialMethod.value &&
		hasUserIncompleteRegistry.value
	)
		return 'complete_social';

	if (
		isUserLoggedIn.value &&
		hasUserRegistryByPhoneMethod.value &&
		hasUserIncompleteRegistry.value
	)
		return 'complete_phone';

	return 'submit';
}

async function sendForm() {
	const nextStep = getNextStep();

	if (nextStep === 'submit') {
		await submitForm();
	}

	await validate();
	if (!meta.value.valid) return;

	if (nextStep === 'complete_phone') {
		await nuxtAuthRepository.completePhoneRegistration({
			name: name.value,
			...(email.value ? { email: email.value } : {})
		});
		await fetchUserSession();
		await submitForm();
	}

	if (nextStep === 'complete_social') {
		await nuxtAuthRepository.completeSocialRegistration({
			phoneNumber: phone.value
		});
		goToOtpFormStep();
	}

	if (nextStep === 'otp') {
		await nuxtAuthRepository.phone({ phoneNumber: phone.value });
		goToOtpFormStep();
	}
}

async function resendOtp(): Promise<void> {
	await nuxtAuthRepository.phone({ phoneNumber: phone.value });
}

const isSubmittingOtpForm = shallowRef<boolean>(false);

const isOTPValid = shallowRef<boolean | null>(null);

async function submitOtpFormForPhoneOrUnlogedUsers(otp: string) {
	try {
		isSubmittingOtpForm.value = true;

		const [_, error] = await nuxtAuthRepository.getCreatePhoneUser({
			phoneNumber: phone.value,
			otp,
			email: email.value || undefined,
			name: name.value || undefined
		});

		if (error) {
			// <!-- TODO: Em caso de erro, mostraremos um toast? -->
			// <!-- TODO: Adicionar track de erro -->
			isSubmittingOtpForm.value = false;
			isOTPValid.value = false;
			return;
		}

		await fetchUserSession();

		isSubmittingOtpForm.value = false;

		await submitForm();
	} catch {
		isSubmittingOtpForm.value = false;
	}
}

async function submitOtpFormForSocialUsers(otp: string) {
	try {
		isSubmittingOtpForm.value = true;

		const [_, error] = await nuxtAuthRepository.verifySocialRegistration({
			otp
		});

		if (error) {
			isSubmittingOtpForm.value = false;
			isOTPValid.value = false;
			return;
		}

		await fetchUserSession();

		isSubmittingOtpForm.value = false;

		await submitForm();
	} catch {
		isSubmittingOtpForm.value = false;
	}
}

function handleOtpFormValidation(event: string) {
	if (
		!isUserLoggedIn.value ||
		(isUserLoggedIn.value && hasUserRegistryByPhoneMethod.value)
	) {
		submitOtpFormForPhoneOrUnlogedUsers(event);
	}

	if (isUserLoggedIn.value && hasUserRegistryBySocialMethod.value) {
		submitOtpFormForSocialUsers(event);
	}
}

const initialUserMessage = computed<string>(() => {
	const options = isPropertyContext.value
		? {
				propertyCommercialId: props.propertyCommercialId,
				propertyRegion: props.propertyRegion,
				propertyType: props.propertyTypeName?.toLowerCase() || ''
			}
		: {
				condoName: props.condoName
			};

	return translateByContext('form.userMessage.initialValue', options);
});

watch(initialUserMessage, message => setFieldValue('userMessage', message), {
	immediate: true
});

const addEventReturnClicked = () => {
	const mapNextStep = {
		'contact-form': 'otp-form',
		'otp-form': 'submit-error-message'
	};

	const stepCurrent = currentStep.value as 'contact-form' | 'otp-form';
	const stepOnClickInPrevious = mapNextStep[stepCurrent] as
		| 'otp-form'
		| 'submit-error-message';

	trackingGoToPrevious({
		[MixpanelProps.CURRENT_PAGE]: currentPageName.value,
		[MixpanelProps.CURRENT_FLOW]: 'lead form',
		[MixpanelProps.CURRENT_STEP]: currentStepNameMapper[stepOnClickInPrevious]
	});
};

const addEventError = () => {
	trackFormPropertyError({
		[MixpanelProps.CONDO_NAME]: props.condoName || null,
		[MixpanelProps.COMMERCIAL_ID]: props.propertyCommercialId || null,
		[MixpanelProps.ERROR_TYPE]: errorMessage.value || ''
	});
};

const addEventsCloseModal = () => {
	trackingCloseClicked({
		[MixpanelProps.CURRENT_PAGE]: currentPageName.value,
		[MixpanelProps.CURRENT_FLOW]: 'lead form',
		[MixpanelProps.CURRENT_STEP]: currentStepNameMapper[currentStep.value]
	});
};

const addEventOtpForm = () => {
	trackFormPropertyAuthentication({
		[MixpanelProps.CONDO_NAME]: props.condoName || null,
		[MixpanelProps.COMMERCIAL_ID]: props.propertyCommercialId || null,
		[MixpanelProps.ASKING_PRICE]: props.askingPrice || null,
		[MixpanelProps.COMPANY_NAME]: props.companyName || null,
		[MixpanelProps.AGENT_NAME]: props.broker?.name || null,
		[MixpanelProps.INPUT_NAME]: name.value,
		[MixpanelProps.INPUT_PHONE]: phone.value,
		[MixpanelProps.INPUT_EMAIL]: email.value || '',
		[MixpanelProps.INPUT_MESSAGE]: userMessage.value || ''
	});
};

const addEventSuccessMessage = () => {
	trackFormPropertyCompleted({
		[MixpanelProps.CONDO_NAME]: props.condoName || null,
		[MixpanelProps.COMMERCIAL_ID]: props.propertyCommercialId || null,
		[MixpanelProps.ASKING_PRICE]: props.askingPrice || null,
		[MixpanelProps.COMPANY_NAME]: props.companyName || null,
		[MixpanelProps.AGENT_NAME]: props.broker?.name || null,
		[MixpanelProps.INPUT_NAME]: name.value,
		[MixpanelProps.INPUT_PHONE]: phone.value,
		[MixpanelProps.INPUT_EMAIL]: email.value || '',
		[MixpanelProps.INPUT_MESSAGE]: userMessage.value || '',
		[MixpanelProps.ACCOUNT_STATUS]: getAccountStatus.value
	});
};

const addEventContactFormOpened = () => {
	trackFormPropertyViewed({
		[MixpanelProps.CONDO_NAME]: props.condoName || null,
		[MixpanelProps.COMMERCIAL_ID]: props.propertyCommercialId || null,
		[MixpanelProps.COMPANY_NAME]: props.companyName || null,
		[MixpanelProps.AGENT_NAME]: props.broker?.name || null,
		[MixpanelProps.ASKING_PRICE]: props.askingPrice || null,
		[MixpanelProps.USER_STATUS]: getAccountStatus.value,
		[MixpanelProps.CURRENT_PAGE]: currentPageName.value
	});
};

const addEventsTermsOrPolicy = (type: string) => {
	const isTerms = type === 'terms';
	const isPolicy = type === 'policy';

	trackSignAndLogin.trackingTermsOrpolicy({
		[MixpanelProps.USER_NAME]: user.value?.name || '',
		[MixpanelProps.USER_EMAIL]: user.value?.email || '',
		[MixpanelProps.USER_PHONE]: user.value?.phoneNumber || '',
		[MixpanelProps.CLICK_FORM]: true,
		[MixpanelProps.TERMS_OF_USE]: isTerms,
		[MixpanelProps.PRIVACY_POLICY]: isPolicy
	});
};

const handleContactMethodClick = (link: ContactMethod) => {
	trackLinkClicked({
		[MixpanelProps.CURRENT_PAGE]: currentPageName.value,
		[MixpanelProps.SECTION_NAME]: 'form property error',
		[MixpanelProps.DESTINATION_URL]: link.route,
		[MixpanelProps.LINK_TEXT]: link.label
	});
};

const getAccountStatus = computed(() => {
	return isUserLoggedIn.value ? 'logged in' : 'logged out';
});

const currentStepNameMapper = {
	'contact-form': 'form modal',
	'otp-form': '2fa authentication',
	'success-message': 'form submitted',
	'submit-error-message': 'error modal'
};

const stepEventsMapper: Record<string, Function[]> = {
	'otp-form': [addEventOtpForm],
	'success-message': [addEventSuccessMessage, resetForm],
	'submit-error-message': [addEventError]
};

watch(currentStep, () => {
	const events = stepEventsMapper[currentStep.value];
	if (events) {
		events.forEach(event => event());
	}
});

watch(
	[() => isOpen.value, () => user.value],
	() => {
		if (!isOpen.value || !user.value) return;
		const userData = user.value;

		if (!userData) return;

		if (!name.value) setFieldValue('name', userData.name || '');
		if (!phone.value) setFieldValue('phone', userData.phoneNumber || '');
		if (!email.value) setFieldValue('email', userData.email || '');
		if (!userMessage.value) {
			setFieldValue('userMessage', initialUserMessage.value);
		}
	},
	{ immediate: true }
);

watch(isOpen, async newValue => {
	if (newValue) {
		await nextTick();

		if (isUserLoggedIn.value) {
			userMessageInput.value?.$el.focus();
			return;
		}

		firstFormInput.value?.$el.focus();

		addEventContactFormOpened();
	}
});

const brokerFirstName = computed<string>(() => getAgentFirstName(props.broker));
const brokerName = computed<string>(() => getAgentName(props.broker));

const modalTitle = computed<string>(() => {
	const i18nOptions = { brokerName: brokerFirstName.value };
	const fieldKey = translateByContext('talkTo');

	if (currentStep.value !== 'contact-form') return t(fieldKey, i18nOptions);

	return isUserLoggedIn.value
		? translateByContext('titleForLoggedUser', i18nOptions)
		: translateByContext('titleForUnloggedUser', i18nOptions);
});

const firstFormInput = useTemplateRef('firstFormInput');
const userMessageInput = useTemplateRef('userMessageInput');

const getImageSrc = useImage();

const brokerSrc = computed(() =>
	getImageSrc(
		props.broker?.profilePicture?.croppedUrl || '',
		{
			format: 'webp',
			width: 128
		},
		{
			provider: 'AWSServerlessImageHandler'
		}
	)
);
</script>

<template>
	<Modal
		v-model:open="isOpen"
		description="property-contact-form-modal"
		:show-close-icon="currentStep !== 'otp-form'"
		content-class="z-[300] border-none w-[90%] md:w-[37.5rem] bg-white text-primary overflow-y-auto no-scrollbar max-h-[95%]"
		overlay-class="z-[60]"
		header-class="font-matter-sq text-xl md:text-2xl font-normal leading-[1.875rem] tracking-[0.02em] text-left text-primary"
		:footer-class="[
			'bg-white border-t border-white-dark-3 sticky bottom-0',
			{
				hidden: currentStep === 'otp-form',
				'p-4 md:px-5': currentStep === 'success-message',
				'px-5 py-3 md:py-2': currentStep === 'contact-form'
			}
		]"
	>
		<template #header>
			<div v-if="currentStep === 'otp-form'">
				<DialogTitle
					class="flex items-center gap-2 font-matter-sq text-xl font-normal tracking-tighter text-primary md:text-2xl"
				>
					<WestIcon class="cursor-pointer" @click="goToContactFormStep" />

					{{ t('phoneOtpTitle') }}
				</DialogTitle>
			</div>
			<DialogTitle
				v-else
				class="font-matter-sq text-2xl font-normal leading-normal tracking-[-0.03rem]"
			>
				{{ modalTitle }}
			</DialogTitle>
		</template>

		<template #default>
			<DialogDescription class="hidden" />

			<section
				v-if="
					['contact-form', 'success-message', 'submit-error-message'].includes(
						currentStep
					) && isPropertyContext
				"
				data-test-id="pdp-contact-form-modal-content"
				class="bg-secondary px-5 py-4 md:px-6"
			>
				<BrokerAvatarInfo
					:border="false"
					avatar-shape="square"
					:show-company-avatar="false"
					:broker-src="brokerSrc || ''"
					avatar-class="h-[52px] w-[52px] md:h-16 md:w-16"
					:info-classes="{
						wrapper:
							'xs-large:max-w-[18.5rem] max-w-40 xs:max-w-56 sm:max-w-sm md:max-w-[24rem] xl:!max-w-[24rem] xs-min:max-w-[11.5rem]',

						brokerName:
							'text-primary font-matter-sq text-base md:text-lg font-normal leading-[150%] tracking-[-0.36px]',
						companyName:
							'text-primary-light-2 font-normal text-xs leading-[150%] text-primary-light-2'
					}"
					:broker-name="brokerName"
					:company-name="companyName"
					class="gap-3 md:gap-4"
				>
					<template #broker>{{ brokerName }}</template>
					<template #company>
						{{ translateByContext('brokerAt', { brokerCompany: companyName }) }}
					</template>
				</BrokerAvatarInfo>

				<template v-if="currentStep === 'contact-form'">
					<Separator class="my-3 bg-secondary-dark-1" />

					<p
						class="w-full text-sm font-light italic leading-[150%] text-primary md:w-[85%]"
					>
						{{ translateByContext('brokerMessage') }}
					</p>
				</template>
			</section>

			<template v-if="currentStep === 'contact-form'">
				<form
					id="property-price-contact-form"
					class="flex flex-col gap-3 bg-white px-5 pb-6 pt-5 md:px-6"
					@submit.prevent="sendForm"
				>
					<Input
						ref="firstFormInput"
						v-model.trim="name"
						v-bind="nameAttrs"
						data-testid="name-input"
						cy-test-id="pdp-name-input"
						:error="errors.name"
						type="text"
						:placeholder="translateByContext('form.name.placeholder')"
						class="h-[3.125rem] text-inherit"
					/>

					<InputPhone
						v-model.trim="phone"
						v-bind="phoneAttrs"
						:error="errors.phone"
						:disabled="!!user?.phoneNumber"
						data-test-id="pdp-phone-input"
						input-class="text-inherit"
					/>

					<Input
						v-model.trim="email"
						v-bind="emailAttrs"
						data-testid="email-input"
						type="email"
						cy-test-id="pdp-mail-input"
						:placeholder="translateByContext('form.email.placeholder')"
						:error="errors.email"
						class="h-[3.125rem] text-inherit"
					/>

					<Textarea
						ref="userMessageInput"
						v-model.trim="userMessage"
						v-bind="userMessageAttrs"
						data-testid="user-message-textarea"
						:placeholder="translateByContext('form.userMessage.placeholder')"
						:error="errors.userMessage"
						class="min-h-[5.625rem] resize-none text-inherit"
					/>
				</form>
			</template>

			<div v-else-if="currentStep === 'otp-form'" class="px-7 pb-8 pt-7">
				<p class="pb-6 text-center">
					{{ t('phoneOtpDescription') }}
				</p>

				<OTPForm
					v-model:is-o-t-p-valid="isOTPValid"
					:phone="phone"
					:is-submitting="isSubmittingOtpForm"
					is-active
					start-countdown-immediately
					@validated="handleOtpFormValidation"
					@change-number="goToContactFormStep"
					@resend="resendOtp"
				/>
			</div>

			<div
				v-else-if="
					['success-message', 'submit-error-message'].includes(currentStep)
				"
				:data-testid="`${currentStep}-step`"
			>
				<div class="flex flex-col items-center gap-4 px-5 py-8">
					<div
						class="flex size-[3.75rem] items-center justify-center rounded-full"
						:class="{
							'bg-success-light-1': currentStep === 'success-message',
							'bg-error-light-1': currentStep === 'submit-error-message'
						}"
					>
						<CheckCircleIcon
							v-if="currentStep === 'success-message'"
							class="size-[3.25rem] text-success"
						/>
						<ErrorCircleIcon v-else class="size-[3.25rem] text-error" />
					</div>

					<div class="flex flex-col items-center justify-center gap-3">
						<strong
							class="text-center font-matter-sq text-lg font-normal leading-normal tracking-[-0.0225rem]"
							:data-test-id="
								currentStep === 'success-message' ? 'pdp-success-message' : ''
							"
						>
							<template v-if="currentStep === 'success-message'">
								{{ t('successConfirmationMessage') }}
							</template>
							<template v-else>
								{{ t('formSubmitErrorMessage') }}
							</template>
						</strong>

						<p class="text-center font-sans text-sm font-normal leading-normal">
							<template v-if="currentStep === 'success-message'">
								{{
									translateByContext(
										'successConfirmationComplementaryMessage',
										{
											brokerName: brokerFirstName
										}
									)
								}}
							</template>
							<template v-else>
								{{ t('formSubmitErrorComplementaryMessage') }}

								<div
									class="flex w-full flex-col items-center justify-center gap-5 pt-5 md:flex-row"
								>
									<NuxtLink
										v-for="link in contactMethodProps"
										:key="link.label"
										:to="link.route"
										target="_blank"
										@click="handleContactMethodClick(link)"
									>
										<TextLink
											as="p"
											class="flex flex-row items-center justify-center gap-2 text-sm font-normal leading-[150%] text-primary underline decoration-black/20 underline-offset-8"
										>
											<Component :is="link.icon" />
											{{ link.label }}
										</TextLink>
									</NuxtLink>
								</div>
							</template>
						</p>
					</div>
				</div>
			</div>

			<div
				v-if="currentStep === 'contact-form' || currentStep === 'otp-form'"
				class="bg-white px-5 py-6 pt-1 text-center text-xs font-normal leading-[1.125rem] text-primary-light-1"
			>
				<I18nT keypath="privacyPolicyStatement" scope="parent" tag="p">
					<template #termsOfUse>
						<NuxtLink
							to="/termos-de-uso"
							class="underline decoration-primary-light-1 underline-offset-[0.2rem]"
							target="_blank"
							@click="addEventsTermsOrPolicy('terms')"
						>
							Termos de Uso</NuxtLink
						>
					</template>
					<template #privacyPolicy>
						<NuxtLink
							to="/politica-de-privacidade"
							class="underline decoration-primary-light-1 underline-offset-[0.2rem]"
							target="_blank"
							@click="addEventsTermsOrPolicy('policy')"
						>
							Política de Privacidade
						</NuxtLink>
					</template>
				</I18nT>
			</div>
		</template>

		<template #footer>
			<Button
				v-if="currentStep === 'contact-form'"
				form="property-price-contact-form"
				size="md"
				class="ml-auto w-fit text-base font-medium"
				:disabled="!meta.valid"
				data-test-id="pdp-send-contact-form-button"
			>
				{{ translateByContext('form.sendButtonText') }}
				<NorthEastIcon class="size-5" />
			</Button>

			<Button
				v-else-if="currentStep === 'success-message'"
				size="md"
				class="ml-auto flex gap-2 text-base font-medium md:w-fit md:gap-3"
				@click="closeModal"
			>
				<DoneIcon class="size-5" />
				{{ t('successConfirmationButtonText') }}
			</Button>

			<Button
				v-else-if="currentStep === 'submit-error-message'"
				size="md"
				class="ml-auto flex gap-2 text-base font-medium md:w-fit md:gap-3"
				@click="goToContactFormStep"
			>
				{{ t('retryButtonText') }}
			</Button>
		</template>
	</Modal>
</template>
